import type { NoiseSpec, SynthSpec } from './synth';

export const FOOTSTEP_SURFACES = ['stone', 'wood', 'metal', 'dirt', 'grass', 'gravel', 'sand', 'snow', 'water'] as const;
export type FootstepSurface = typeof FOOTSTEP_SURFACES[number];
export const FOOTSTEP_VARIANTS = 4;
export const footstepId = (surface: FootstepSurface, variant: number) => `footstep.${surface}.${variant}`;
export const FOOTSTEP_IDS = FOOTSTEP_SURFACES.flatMap(s => Array.from({ length: FOOTSTEP_VARIANTS }, (_, i) => footstepId(s, i)));
export const landingId = (surface: FootstepSurface, variant: number) => `land.${surface}.${variant}`;
export const LANDING_IDS = FOOTSTEP_SURFACES.flatMap(s => Array.from({ length: FOOTSTEP_VARIANTS }, (_, i) => landingId(s, i)));

/** Soft sole contact, muted material resonance, then light friction. Baked once
 * per variant by SoundBank, never synthesized on the render/audio hot path. */
export function footstepSynth(surface: FootstepSurface, variant: number): SynthSpec {
  const v = 0.91 + variant * 0.06;
  const soft = ['grass', 'sand', 'snow', 'water'].includes(surface);
  // A short cushioned envelope replaces the sharp, pitch-dropping heel thud.
  const layers: SynthSpec[] = [{ kind: 'tone', type: 'sine', dur: .085,
    freq: (soft ? 90 : 120) * v, freqEnd: 80, attack: .012, decay: .073, gain: soft ? .035 : .065 }];
  const noise = (freq: number, dur: number, gain: number, delay = 0, q = 0.7): NoiseSpec =>
    ({ kind: 'noise', color: 'pink', dur, freq: freq * v, type: 'bandpass', q, gain, delay, attack: .009, decay: dur - .009 });
  const ring = (freq: number, dur: number, gain: number): SynthSpec =>
    ({ kind: 'tone', type: 'sine', dur: dur * .65, freq: freq * v, gain: gain * .4, attack: .008, decay: dur * .65 - .008 });
  switch (surface) {
    case 'stone': layers.push(noise(1700, .045, .30), noise(1100, .035, .10, .035)); break;
    case 'wood': layers.push(noise(1250, .025, .28), ring(260, .075, .17), ring(610, .045, .07), noise(1800, .03, .10, .043)); break;
    case 'metal': layers.push(noise(2400, .032, .20), ring(430, .17, .10), ring(977, .12, .08), ring(1733, .085, .04)); break;
    case 'dirt': layers.push(noise(700, .075, .30), noise(1900, .06, .14, .045)); break;
    case 'grass': layers.push(noise(2400, .13, .20), noise(1600, .09, .18, .043)); break;
    case 'gravel':
      layers.push(noise(1400, .065, .26));
      for (let i = 0; i < 4; i++) layers.push(noise(1200 + ((i * 791 + variant * 317) % 1500), .022 + (i % 3) * .005, .09, .015 + i * (.018 + variant * .001)));
      break;
    case 'sand': layers.push(noise(950, .17, .27), noise(3100, .09, .09, .035)); break;
    case 'snow': layers.push(noise(1800, .13, .30, 0, 1.5), noise(650, .16, .25, .025, 1.2)); break;
    case 'water': layers.push({ ...noise(1100, .16, .28, 0, 1), freqEnd: 650 }, noise(2300, .07, .10, .045)); break;
  }
  return { kind: 'layers', dur: .28, gain: .85, layers };
}

/** One foot bearing a landing: preserve its material signature and add the
 * body's weight. Both feet are played separately so mixed floors still work. */
export function landingSynth(surface: FootstepSurface, variant: number): SynthSpec {
  const soft = ['grass', 'sand', 'snow', 'water'].includes(surface);
  return { kind: 'layers', dur: .34, gain: .85, layers: [
    footstepSynth(surface, variant),
    { kind: 'tone', type: 'sine', dur: .12, freq: (soft ? 85 : 105) * (.94 + variant * .04),
      freqEnd: 72, attack: .014, decay: .106, gain: soft ? .03 : .055 },
  ] };
}

export interface FootstepMotion {
  dt: number; active: boolean; onFoot: boolean; mode: string; grounded: boolean;
  jet: string; speed: number; waterState: string; crouching: boolean; iframeT: number;
  /** Contacts from this rendered pose: bit 0 left, bit 1 right. */
  footfalls?: number;
}

/** The animation owns timing and side. Audio only chooses variation/intensity;
 * no independent clock, accumulated distance, or assumed foot alternation. */
export class FootstepPlayback {
  private variant = -1;
  private nextVariant(): number {
    this.variant = (this.variant + 1 + Math.floor(Math.random() * (FOOTSTEP_VARIANTS - 1))) % FOOTSTEP_VARIANTS;
    return this.variant;
  }

  landing(impactSpeed: number): { side: 0 | 1; variant: number; gain: number; rate: number }[] {
    if (!Number.isFinite(impactSpeed) || impactSpeed < 0) return [];
    const weight = Math.max(0, Math.min(1, (impactSpeed - 2) / 12));
    return ([0, 1] as const).map(side => ({
      side, variant: this.nextVariant(), gain: .38 + .32 * weight, rate: 1 - .06 * weight,
    }));
  }
  update(f: FootstepMotion): { side: 0 | 1; variant: number; gain: number }[] {
    if (!f.active || !f.onFoot || f.mode !== 'foot' || !f.grounded || f.jet !== 'none'
      || f.waterState === 'swim' || f.iframeT > 0 || !Number.isFinite(f.speed) || f.speed <= .6
      || !Number.isFinite(f.dt) || f.dt <= 0 || f.dt > .25) return [];
    const steps: { side: 0 | 1; variant: number; gain: number }[] = [];
    for (const side of [0, 1] as const) {
      if (!((f.footfalls ?? 0) & (1 << side))) continue;
      steps.push({ side, variant: this.nextVariant(), gain: Math.min(1, .5 + f.speed / 12) * (f.crouching ? .45 : 1) });
    }
    return steps;
  }
}
